//TO DO: in the next PR refactor and test this context
import {
    createContext,
    ReactNode,
    useContext,
    useState,
    useEffect,
    SetStateAction,
    Dispatch,
    useCallback
} from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useIntl } from "react-intl";
import { v4 as createUUID } from "uuid";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    operateWithSignupUFOLogging,
    SignupGetUFOErrorType,
    SignupUFOLoggingActionTimeoutError,
    trackAndCompleteUFOEvent,
    ufoEventExistingSiteN2ESubmit,
    ufoEventGraphQLWebhookSetup,
    UfoEventName,
    ufoEventNewSiteN2NSubmit,
    UfoLabelStack,
    trackSubmitSuccess,
    trackSignupStarted,
    trackSiteNameChangedFromRecommendedSiteName,
    trackSitePickerSubmit,
    getCrossFlowActivationUrl,
    UfoExperienceStatus,
    UfoInteractionType,
    getRedirectUrlFromShadowItError,
    isTestTenantByName,
    mapProductKeyAndEditionToCcpProduct,
    getRecaptcha,
    getConfirmationPageUrl,
    getSignupContext,
    getConsents,
    ProductKey,
    trackRecaptchaFailure
} from "@/components/signup/utils";
import {
    useSitesWithoutCurrentProduct,
    useUserContext
} from "@/components/user-context";
import {
    AisSitesType,
    ExistingSite,
    Platform,
    UserSite,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src/hook";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Cookie } from "@/utils/cookie";

import {
    messages as validationMessages,
    FormattedMessageProps
} from "../messages";

const { ABORTED, SUCCEEDED } = UfoExperienceStatus;
const { PRESS, TRANSITION } = UfoInteractionType;
const ufoBaseLogs: UfoLabelStack = [
    { name: "pkg:site-activation" },
    { name: "component:siteActivationContextProvider" }
];
export interface SiteActivationContext {
    activateSite: ({ privacyPolicy }: { privacyPolicy?: string }) => void;
    sitesWithoutCurrentProduct: ExistingSite[];
    isRequestInProgress: boolean;
    showSitePicker: boolean;
    email?: string;
    sitePickerValue: AisSitesType | UserSite | undefined;
    setSitePickerValue: Dispatch<
        SetStateAction<AisSitesType | UserSite | undefined>
    >;
    setSiteNameValid: Dispatch<SetStateAction<boolean>>;
    siteName: string;
    setSiteNameValue: Dispatch<SetStateAction<string>>;
    isSubmitting: boolean;
    hasSubmitted: boolean;
    setHasSubmitted: Dispatch<SetStateAction<boolean>>;
    validationMessageState: [
        FormattedMessageProps | undefined,
        Dispatch<SetStateAction<FormattedMessageProps | undefined>>
    ];
    setShowSitePicker: Dispatch<SetStateAction<boolean>>;
    recommendedSiteName: string;
}

export const SiteActivationContext =
    createContext<SiteActivationContext | null>(null);

export const useSiteActivationContext = (): SiteActivationContext => {
    const context = useContext(SiteActivationContext);
    if (!context) {
        throw new Error(
            "useSiteActivationContext must be used within a SiteActivationContextProvider"
        );
    }
    return context;
};

export const SiteActivationContextProvider = ({
    children
}: {
    children: ReactNode;
}) => {
    //HOOKS
    const intl = useIntl();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const signupClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { profile, user, product, edition } = useUserContext();
    const sitesWithoutCurrentProduct = useSitesWithoutCurrentProduct();

    //STATE
    const [showSitePicker, setShowSitePicker] = useState(
        sitesWithoutCurrentProduct.length > 0
    );
    const [siteNameValid, setSiteNameValid] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sitePickerValue, setSitePickerValue] = useState<
        AisSitesType | UserSite | undefined
    >(sitesWithoutCurrentProduct[0]);
    const [siteName, setSiteNameValue] = useState("");
    const [recommendedSiteName] = useState("");
    const [recaptchaFailedToLoad, setRecaptchFailedToLoad] =
        useState<boolean>(false);
    const [
        recaptchaRetryTimeRemainingSecond,
        setRecaptchaRetryTimeRemainingSecond
    ] = useState(60);

    const [validatationMessage, setValidationMessage] = useState<
        undefined | FormattedMessageProps
    >();

    //VARIABLES
    const searchParameters = new URLSearchParams(window.location.search);
    const platform = (searchParameters.get("platform") ??
        "routing") as Platform;
    const localizedContactUrl = useLocalizedHref("/company/contact");
    const isNewUser = profile.cohorts?.userType === "new-user";
    const productKey = product?.key as ProductKey;

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const redirectToAdminHubForShadowIt = (error: any) => {
        const errorCode = error?.response?.status;
        const redirectUrlShadowIt =
            errorCode === 403 && getRedirectUrlFromShadowItError(error);

        // Shadow IT unauthorized user redirects to adminhub's Shadow IT request flow.
        if (errorCode === 403 && Boolean(redirectUrlShadowIt)) {
            window.location.assign(redirectUrlShadowIt as string);
        }
    };

    const activateSiteHandler = useCallback(
        // eslint-disable-next-line complexity
        async (signupId?: string, privacyPolicy?: string) => {
            setIsSubmitting(true);

            if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
                // set trigger that will wait for Recaptcha retry logic
                setRecaptchFailedToLoad(true);
                return;
            }
            setRecaptchFailedToLoad(false);

            const localeData = await signupClient.getConsentConfig();

            const testingPurposes = isTestTenantByName(siteName);
            const signupContext = getSignupContext({
                productKey,
                testingPurposes,
                signupId,
                search: location.search
            });

            const MAX_SIGNUP_CONTEXT_LENGTH = 8192;
            let signupContextString: string = JSON.stringify(signupContext);
            if (signupContextString.length > MAX_SIGNUP_CONTEXT_LENGTH) {
                signupContextString = "{}";
            }
            const ccpProducts = mapProductKeyAndEditionToCcpProduct({
                productKey,
                edition: edition as string
            });

            const gRecaptchaResponse = await getRecaptcha(
                user.email || "",
                executeRecaptcha
            );

            ufoEventNewSiteN2NSubmit.setCustomData({
                gRecaptchaResponse: gRecaptchaResponse ?? "none",
                executeRecaptchaExists: Boolean(executeRecaptcha),
                reactComponent: "SiteActivationProvider"
            });

            // Handle missing reCAPTCHA response
            if (!gRecaptchaResponse) {
                trackRecaptchaFailure(createAnalyticsEvent, "site-activation");
                const invalidRecaptchaError = new Error(
                    "Missing reCAPTCHA token"
                );
                ufoEventNewSiteN2NSubmit.failure({
                    customData: {
                        reactComponent: "SiteActivationProvider",
                        analyticsEvent: "errored",
                        siteType: "new-site"
                    },
                    error: invalidRecaptchaError
                });
                throw invalidRecaptchaError;
            }

            // Set experiment metadata before starting UFO event for GraphQL webhook setup
            ufoEventGraphQLWebhookSetup.setCustomData({
                webPlatform: "site-activation"
            });

            try {
                const response = await signupClient.activateProduct({
                    displayName: user.name,
                    email: user.email || "",
                    ccpProducts: [ccpProducts],
                    siteName,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    useCcpPlatform: platform === "ccp",
                    consent: getConsents(
                        localeData.locale ?? "US-VA",
                        localeData.localeRequiresMarketingCommunicationOptIn,
                        privacyPolicy
                    ),
                    platform,
                    signupContext: signupContextString,
                    isNewUser:
                        productKey === "jira-software.ondemand" || isNewUser,
                    gRecaptchaResponse,
                    testingPurposes,
                    signupId
                });

                const redirectUrl = getConfirmationPageUrl({
                    locale: intl.locale,
                    productKey,
                    response,
                    siteName,
                    signupId,
                    edition: edition as string
                });
                window.location.assign(redirectUrl);
                //@ts-ignore
            } catch (error: never) {
                // SLO "bad event" for N2N signup submit
                ufoEventNewSiteN2NSubmit.failure({
                    customData: {
                        reactComponent: "SiteActivationProvider",
                        analyticsEvent: "errored",
                        siteType: "new-site"
                    },
                    error
                });

                redirectToAdminHubForShadowIt(error);

                console.log(
                    `Error occurred while activateProduct API is called`,
                    error
                );
                const errorCode = error?.response?.status;
                const errorId = error?.response?.headers?.get("atl-traceid");

                if (errorCode === 409) {
                    setIsSubmitting(false);
                } else {
                    setValidationMessage({
                        ...validationMessages.timeout,
                        //@ts-ignore
                        values: {
                            // eslint-disable-next-line react/no-unstable-nested-components
                            "href-support": (node: ReactNode[]) => (
                                <a href={localizedContactUrl}>{node}</a>
                            ),
                            "error_id": errorId ?? ""
                        }
                    });
                    const isRecaptchaNotFoundError =
                        errorCode === 400 &&
                        error.message &&
                        error.message.includes("Recaptcha was not found");
                    if (isRecaptchaNotFoundError) {
                        setIsSubmitting(false);
                    }
                }
            }
        },
        [
            executeRecaptcha,
            recaptchaRetryTimeRemainingSecond,
            signupClient,
            siteName,
            productKey,
            edition,
            user,
            platform,
            isNewUser,
            intl.locale,
            localizedContactUrl
        ]
    );

    useEffect(() => {
        const showRecaptchaFormError = () => {
            setValidationMessage({
                ...validationMessages.timeout,
                //@ts-ignore
                values: {
                    // eslint-disable-next-line react/no-unstable-nested-components
                    "href-support": (node: ReactNode[]) => (
                        <a href={localizedContactUrl}>{node}</a>
                    ),
                    "error_id":
                        Cookie.readCookie("ajs_anonymous_id") ||
                        "Invalid Recaptcha"
                }
            });

            setIsSubmitting(false);
            return;
        };
        const ufoSubmitEvent = showSitePicker
            ? ufoEventExistingSiteN2ESubmit
            : ufoEventNewSiteN2NSubmit;

        if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
            ufoSubmitEvent.failure({
                customData: {
                    reactComponent: "SiteActivationContextProvider",
                    analyticsEvent: "errored",
                    recaptchaRetryTimeRemainingSecond: 0,
                    siteType: showSitePicker ? "existing-site" : "new-site"
                },
                error: new Error("Recaptcha retry failed")
            });
        }
        if (recaptchaFailedToLoad) {
            activateSiteHandler()
                .then(() => {
                    ufoSubmitEvent.success({
                        customData: {
                            reactComponent: "SiteActivationContextProvider",
                            analyticsEvent: "succeeded",
                            siteType: showSitePicker
                                ? "existing-site"
                                : "new-site",
                            recaptchaRetryTimeRemainingSecond
                        }
                    });
                })
                .catch((error) => {
                    showRecaptchaFormError();
                    ufoSubmitEvent.failure({
                        customData: {
                            reactComponent: "SiteActivationContextProvider",
                            analyticsEvent: "errored",
                            siteType: showSitePicker
                                ? "existing-site"
                                : "new-site",
                            recaptchaRetryTimeRemainingSecond
                        },
                        error
                    });
                    return;
                });
        }
    }, [
        activateSiteHandler,
        executeRecaptcha,
        localizedContactUrl,
        recaptchaFailedToLoad,
        recaptchaRetryTimeRemainingSecond,
        showSitePicker
    ]);

    useEffect(() => {
        if (!recaptchaFailedToLoad) return;
        if (recaptchaRetryTimeRemainingSecond <= 0) return;
        const intervalId = setInterval(() => {
            setRecaptchaRetryTimeRemainingSecond(
                recaptchaRetryTimeRemainingSecond - 1
            );
        }, 1000);
        return () => clearInterval(intervalId);
    }, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

    const activateSite = async ({
        privacyPolicy
    }: {
        privacyPolicy?: string;
    }) => {
        setHasSubmitted(true);
        sessionStorage.setItem("provisioningStartTime", `${Date.now()}`);
        // Start UFOs to begin duration metrics for signup submit
        ufoEventNewSiteN2NSubmit.start({
            productKey,
            webPlatform: "site-activation"
        });
        ufoEventExistingSiteN2ESubmit.start({
            productKey,
            webPlatform: "site-activation"
        });
        const ufoLogs: UfoLabelStack = [
            ...ufoBaseLogs,
            { name: "method:activateSite" }
        ];

        // user must be logged in
        if (!user.isUserLoggedIn) return;

        // Generate a signup ID and add it to all the events in this flow.
        // This id is used to stitch together a view of the end-to-end
        // provisioning experience, all the way to landing in product.
        const signupId = createUUID();
        const isN2N = !showSitePicker && !sitePickerValue;
        const domainType = profile && profile.cohorts?.domainType;
        trackSignupStarted(
            createAnalyticsEvent,
            signupId,
            productKey,
            edition,
            isN2N,
            domainType
        );

        // handle site picker submit
        if (showSitePicker) {
            if (sitePickerValue) {
                try {
                    // Check for Shadow IT authorization.
                    await signupClient.getActivationAuthorization({
                        productKeys: [productKey],
                        productEdition: edition as string,
                        cloudId: sitePickerValue.cloudId,
                        siteName: sitePickerValue?.displayName
                    });

                    const crossFlowActivationUrl = getCrossFlowActivationUrl({
                        cloudId: sitePickerValue.cloudId,
                        edition,
                        productKey,
                        siteName: sitePickerValue?.displayName
                    });

                    ufoEventExistingSiteN2ESubmit.success({
                        customData: {
                            reactComponent: "SiteActivationContextProvider",
                            analyticsEvent: "succeeded",
                            siteType: "existing-site"
                        }
                    });

                    trackSitePickerSubmit({
                        createAnalyticsEvent,
                        targetCloudId: sitePickerValue.cloudId,
                        provisionedProducts: [{ product: productKey, edition }],
                        signupUrl: crossFlowActivationUrl,
                        aaid: user.accountId
                    });

                    window.location.assign(crossFlowActivationUrl);
                } catch (error) {
                    ufoEventExistingSiteN2ESubmit.failure({
                        customData: {
                            reactComponent: "SiteActivationContextProvider",
                            analyticsEvent: "errored",
                            siteType: "existing-site"
                        },
                        error
                    });

                    redirectToAdminHubForShadowIt(error);
                }
            }
            return;
        }

        if (!siteNameValid || !siteName) {
            setHasSubmitted(false); // reset to allow re-submit
            return;
        }

        trackSubmitSuccess(createAnalyticsEvent);

        if (siteName !== recommendedSiteName) {
            trackSiteNameChangedFromRecommendedSiteName(
                createAnalyticsEvent,
                siteName,
                recommendedSiteName
            );
        }

        let setTimeoutId: ReturnType<typeof setTimeout>;

        const interactionId = createUUID();
        operateWithSignupUFOLogging(
            {
                interactionId,
                interactionName: "try-cloud-signup_form-submit",
                type: "press",
                timeout: 2 * 60 * 1000,
                labelStack: [{ name: "uip" }],
                errorContext: "error occured on signup form submit button"
            },
            async () =>
                Promise.race([
                    new Promise((_resolve, reject) => {
                        setTimeoutId = setTimeout(() => {
                            reject(SignupUFOLoggingActionTimeoutError);
                        }, 60000);
                    }),
                    activateSiteHandler(signupId, privacyPolicy).finally(() => {
                        if (setTimeoutId) {
                            clearTimeout(setTimeoutId);
                        }
                    })
                ])
        )
            .then(() => {
                // SLO "good event" for N2N signup submit
                ufoEventNewSiteN2NSubmit.success({
                    customData: {
                        reactComponent: "SiteActivationContextProvider",
                        analyticsEvent: "succeeded",
                        siteType: "new-site"
                    }
                });

                trackAndCompleteUFOEvent({
                    experienceName: UfoEventName.ACTIVATE_PRODUCT_API_SUCCESS,
                    experienceStatus: SUCCEEDED,
                    interactionType: TRANSITION,
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "good event" for signup submit captured on bxp-ui only
                trackAndCompleteUFOEvent({
                    experienceName:
                        UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_SUCCEEDED,
                    experienceStatus: SUCCEEDED,
                    interactionType: PRESS,
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "good event" for signup submit captured across bxp-ui and bxpkit
                trackAndCompleteUFOEvent({
                    experienceName:
                        UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_SUCCEEDED,
                    experienceStatus: SUCCEEDED,
                    interactionType: PRESS,
                    ufoLogger: ufoLogs
                });
            })
            .catch((error) => {
                // SLO "bad event" for N2N signup submit
                ufoEventNewSiteN2NSubmit.failure({
                    customData: {
                        reactComponent: "SiteActivationContextProvider",
                        analyticsEvent: "errored",
                        siteType: "new-site"
                    },
                    error
                });

                const errorType = SignupGetUFOErrorType(error);
                if (errorType !== "http_exception") {
                    setValidationMessage({
                        ...validationMessages.timeout,
                        //@ts-ignore
                        values: {
                            // eslint-disable-next-line react/no-unstable-nested-components
                            "href-support": (node: ReactNode[]) => (
                                <a href={localizedContactUrl}>{node}</a>
                            ),
                            "error_id": `Interaction_Error:${interactionId}`
                        }
                    });
                    // Reset form to allow re-submit
                    setIsSubmitting(false);
                }
                trackAndCompleteUFOEvent({
                    experienceName: UfoEventName.ACTIVATE_PRODUCT_API_ERROR,
                    experienceStatus: ABORTED,
                    interactionType: TRANSITION,
                    error,
                    errorName: "ActivateProductError",
                    errorMessage: "error occured on activateProduct",
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "bad event" for signup submit captured on bxp-ui only
                trackAndCompleteUFOEvent({
                    experienceName: UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_FAILED,
                    experienceStatus: ABORTED,
                    interactionType: PRESS,
                    error,
                    errorName: "ActivateProductError",
                    errorMessage: "error occured on activateProduct",
                    ufoLogger: ufoLogs
                });

                // Reliabiltiy SLO "bad event" for signup submit captured across bxp-ui and bxpkit
                trackAndCompleteUFOEvent({
                    experienceName:
                        UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_FAILED,
                    experienceStatus: ABORTED,
                    interactionType: PRESS,
                    error,
                    errorName: "ActivateProductError",
                    errorMessage: "error occured on activateProduct",
                    ufoLogger: ufoLogs
                });
            });
    };

    return (
        <SiteActivationContext.Provider
            value={{
                isSubmitting,
                activateSite,
                showSitePicker,
                sitesWithoutCurrentProduct: sitesWithoutCurrentProduct,
                isRequestInProgress: profile.requestInProgress,
                email: user.email,
                sitePickerValue,
                setSitePickerValue,
                setSiteNameValid,
                siteName,
                setSiteNameValue,
                setShowSitePicker,
                recommendedSiteName,
                hasSubmitted,
                setHasSubmitted,
                validationMessageState: [
                    validatationMessage,
                    setValidationMessage
                ]
            }}
        >
            {children}
        </SiteActivationContext.Provider>
    );
};
